import {
  APIEntityType,
  FilterTypes,
  FilterAttributes,
  LabelFormattingBaseAttributes,
} from '@ardoq/api-types';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import {
  ComponentOrReferenceFormattingRule,
  FormattingRule,
  TagFormattingRule,
} from '@ardoq/perspectives';

const isCompleteFormattingRule = (rule: FormattingRule): boolean => {
  if (rule.type === APIEntityType.TAG) {
    return true;
  }
  // Component and Reference rules must have a field selected
  return rule.field !== null;
};

export const formattingRulesToFormattingAttributes = (
  formattingRules: FormattingRule[]
): FilterAttributes[] =>
  formattingRules.filter(isCompleteFormattingRule).map(formattingRule => {
    switch (formattingRule.type) {
      case APIEntityType.TAG:
        return toTagFormattingAttributes(formattingRule);
      case APIEntityType.COMPONENT:
        return toComponentFormattingAttributes(formattingRule);
      case APIEntityType.REFERENCE:
        return toReferenceFormattingAttributes(formattingRule);
      default:
        return exhaustiveCheck(formattingRule);
    }
  });

// No types → neither field (rule applies to all types).
// Otherwise → affectsTypeNames (consumers use legacyAffectsTypeNames to handle both fields).
const toTypeNameFields = (
  typeNames: string[] | null
): Pick<FilterAttributes, 'affectsTypeNames'> => {
  if (!typeNames || typeNames.length === 0) return {};
  return { affectsTypeNames: typeNames };
};

const toTagFormattingAttributes = (
  tagFormattingRule: TagFormattingRule
): FilterAttributes => ({
  affectComponent: true,
  affectReference: true,
  type: FilterTypes.TAG,
  color: tagFormattingRule.color,
  isNegative: tagFormattingRule.isInverted,
  isCollapsed: tagFormattingRule.isCollapsed,
  name: tagFormattingRule.value ?? undefined,
  value: tagFormattingRule.value,
  version: tagFormattingRule.version ?? 0,
});

const toComponentFormattingAttributes = (
  rule: ComponentOrReferenceFormattingRule
): FilterAttributes => ({
  affectComponent: true,
  affectReference: false,
  color: rule.color,
  isNegative: rule.isInverted,
  isCollapsed: rule.isCollapsed,
  ...toTypeNameFields(rule.typeNames),
  value: rule.value,
  name: rule.field ?? undefined,
  type: FilterTypes.ATTRIBUTE,
  comparator: rule.comparator,
  version: rule.version ?? 0,
});

const toReferenceFormattingAttributes = (
  rule: ComponentOrReferenceFormattingRule
): FilterAttributes => ({
  affectComponent: false,
  affectReference: true,
  color: rule.color,
  isNegative: rule.isInverted,
  isCollapsed: rule.isCollapsed,
  ...toTypeNameFields(rule.typeNames),
  value: rule.value,
  name: rule.field ?? undefined,
  type: FilterTypes.ATTRIBUTE,
  comparator: rule.comparator,
  version: rule.version ?? 0,
});

export const getComponentLabelFormattingFilterAttributes = (
  componentLabelFormattingValue: string,
  includeTime: boolean | undefined
): LabelFormattingBaseAttributes & {
  type: FilterTypes.COMPONENT_LABEL;
  affectComponent: true;
  affectReference: false;
} => {
  return {
    affectComponent: true,
    affectReference: false,
    isNegative: false,
    name: '',
    type: FilterTypes.COMPONENT_LABEL,
    value: componentLabelFormattingValue,
    ...(includeTime ? { includeTime } : {}),
  };
};

export const getReferenceLabelFormattingFilterAttributes = (
  referenceLabelFormattingValue: string,
  includeTime: boolean | undefined
): LabelFormattingBaseAttributes & {
  type: FilterTypes.REFERENCE_LABEL;
  affectReference: true;
  affectComponent: false;
} => {
  return {
    affectComponent: false,
    affectReference: true,
    isNegative: false,
    name: '',
    type: FilterTypes.REFERENCE_LABEL,
    value: referenceLabelFormattingValue,
    ...(includeTime ? { includeTime } : {}),
  };
};
